/**
 * Song tabs: the doc of the signed-in user, kept on this device in the base Dexie DB ('kgr', table
 * kv, key `tabs:v1:<userId>`) — ids only (model.ts), so it works offline from saved data and nothing
 * of it is readable by the next user of a shared iPad: the doc names its user (another user's doc
 * reads as empty), and the whole 'kgr' DB plus this in-memory copy go on logout / user switch
 * (offline/wipe.ts wipeLocalData → resetTabsMemory).
 *
 * A small external store (useSyncExternalStore): only the strip subscribes, so a page turn (which
 * updates the tab's page memo) re-renders the strip, never the sheet. Changes made before the
 * stored doc has been read (the first song page of a cold start) are queued and applied on top of
 * it. Writes are debounced (WRITE_DELAY_MS) and flushed when the page is hidden or closed. Nothing
 * is written before the stored doc has been read (a write then would replace tabs not read yet):
 * when IndexedDB is slow (no answer within READ_TIMEOUT_MS) the strip shows the session's tabs and
 * they are merged into the stored ones once the read answers; when it is unavailable (the read
 * fails) the tabs live for the session.
 */
import { useSyncExternalStore } from 'react';
import { localDb } from '../db/dexie';
import { emptyDoc, parseDoc, type TabsDoc } from './model';

export const TABS_KV_PREFIX = 'tabs:v1:';
export const WRITE_DELAY_MS = 300;

/** When this page load started: lists fetched since then are fresh enough to prune tabs (useTabSync). */
export const SESSION_START = Date.now();

export interface TabsState {
  /** Whose tabs are in memory (null: nobody's — before the song page, or after a wipe). */
  userId: string | null;
  doc: TabsDoc | null;
  /** The stored doc has been read (the strip waits for it, so it never pops in tab by tab). */
  loaded: boolean;
}

const EMPTY: TabsState = { userId: null, doc: null, loaded: false };

let state: TabsState = EMPTY;
/** Changes asked for before the stored doc was read. */
let queued: ((doc: TabsDoc) => TabsDoc)[] = [];
/**
 * The stored doc: being read ('reading' — changes are queued for it and nothing is written), read
 * ('read' — writes go through), or unreadable ('failed' — the tabs live for the session).
 */
let readState: 'reading' | 'read' | 'failed' = 'read';
/** Bumped by every user change / wipe: a read or write of an older generation is dropped. */
let generation = 0;
let writeTimer: ReturnType<typeof setTimeout> | null = null;
let dirty = false;
let lifecycleHooked = false;
const listeners = new Set<() => void>();

const kvKey = (userId: string) => `${TABS_KV_PREFIX}${userId}`;

function setState(next: TabsState): void {
  state = next;
  for (const l of [...listeners]) l();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The current state (same object until it changes). */
export function getTabsState(): TabsState {
  return state;
}

/** The tabs in memory; re-renders on every change. */
export function useTabsState(): TabsState {
  return useSyncExternalStore(subscribe, getTabsState, getTabsState);
}

/**
 * A read that has not answered by then (IndexedDB stuck or slow, as iOS sometimes is): the strip
 * shows the session's tabs meanwhile; they are merged into the stored ones if the read answers later.
 */
export const READ_TIMEOUT_MS = 2000;

/** The stored doc (empty when there is none, or another user's); null when IndexedDB cannot be read. */
async function readDoc(userId: string): Promise<TabsDoc | null> {
  try {
    return parseDoc((await localDb().kv.get(kvKey(userId)))?.v, userId);
  } catch {
    return null;
  }
}

function hookLifecycle(): void {
  if (lifecycleHooked || typeof window === 'undefined' || typeof document === 'undefined') return;
  lifecycleHooked = true;
  // iOS may kill a backgrounded Home-Screen app without a pagehide: hidden is the last sure moment.
  window.addEventListener('pagehide', flushTabs);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushTabs();
  });
}

/**
 * Makes `userId`'s tabs the ones in memory: read from storage once per page load (another user's
 * copy is dropped first). Call from an effect (it notifies the subscribers).
 */
export function loadTabs(userId: string): void {
  if (state.userId === userId) return;
  hookLifecycle();
  const gen = ++generation;
  cancelWrite();
  queued = [];
  readState = 'reading';
  setState({ userId, doc: emptyDoc(userId), loaded: false });
  const replay = (base: TabsDoc) => queued.reduce((d, fn) => fn(d), base);
  // Slow read: show the session's tabs (display only — nothing is written until the read answers).
  const timer = setTimeout(() => {
    if (gen === generation && !state.loaded) setState({ userId, doc: replay(emptyDoc(userId)), loaded: true });
  }, READ_TIMEOUT_MS);
  void readDoc(userId).then((stored) => {
    clearTimeout(timer);
    if (gen !== generation) return;
    const changed = queued.length > 0;
    readState = stored ? 'read' : 'failed';
    // Unreadable: keep what the session shows (after a timeout it already holds every change).
    const doc = stored ? replay(stored) : state.loaded && state.doc ? state.doc : replay(emptyDoc(userId));
    queued = [];
    setState({ userId, doc, loaded: true });
    if (changed && stored) scheduleWrite();
  });
}

/**
 * Applies a model change (model.ts) to `userId`'s tabs. Ignored while another user's tabs are loaded;
 * with none loaded (a wipe while a song page stays open: a user switch on a deep link) the user's
 * tabs are read again first.
 */
export function updateTabs(userId: string, fn: (doc: TabsDoc) => TabsDoc): void {
  if (state.userId === null) loadTabs(userId);
  if (state.userId !== userId || !state.doc) return;
  // Kept for the stored doc while it is being read (also after a timeout: shown now, replayed then).
  if (readState === 'reading') queued.push(fn);
  if (!state.loaded) return;
  const next = fn(state.doc);
  if (next === state.doc) return;
  setState({ ...state, doc: next });
  scheduleWrite();
}

function cancelWrite(): void {
  if (writeTimer) clearTimeout(writeTimer);
  writeTimer = null;
  dirty = false;
}

function scheduleWrite(): void {
  dirty = true;
  if (writeTimer) clearTimeout(writeTimer);
  writeTimer = setTimeout(flushTabs, WRITE_DELAY_MS);
}

/** Writes a pending change now (page hidden / closed). Never throws. */
export function flushTabs(): void {
  if (writeTimer) clearTimeout(writeTimer);
  writeTimer = null;
  const { doc, loaded } = state;
  if (!dirty || !doc || !loaded || readState !== 'read') return;
  dirty = false;
  const gen = generation;
  void (async () => {
    try {
      if (gen !== generation) return;
      await localDb().kv.put({ k: kvKey(doc.userId), v: doc });
    } catch {
      /* IndexedDB unavailable: kept in memory for this session */
    }
  })();
}

/**
 * Logout / user switch (wipeLocalData): forget the in-memory tabs and drop any pending or in-flight
 * read and write, so nothing of the previous user is written back after the 'kgr' DB is deleted.
 */
export function resetTabsMemory(): void {
  generation++;
  cancelWrite();
  queued = [];
  if (state !== EMPTY) setState(EMPTY);
}
